import { NodeViewWrapper, NodeViewProps } from '@tiptap/react'
import { useState, useRef, useCallback } from 'react'
import { X, Braces } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import VariablePicker from './VariablePicker'
import { Variable } from '@/lib/variables'

export default function ButtonNodeView({ node, updateAttributes, deleteNode }: NodeViewProps) {
  const [textVarOpen, setTextVarOpen] = useState(false)
  const [urlVarOpen, setUrlVarOpen] = useState(false)
  const textInputRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)

  const { text, url } = node.attrs as { text: string; url: string }

  const insertVariableAtCursor = useCallback(
    (inputRef: React.RefObject<HTMLInputElement | null>, attr: 'text' | 'url', variable: Variable) => {
      const input = inputRef.current
      if (!input) return

      const currentValue = attr === 'text' ? text : url
      const start = input.selectionStart ?? currentValue.length
      const end = input.selectionEnd ?? currentValue.length
      const tag = `{{${variable.key}}}`
      const newValue = currentValue.slice(0, start) + tag + currentValue.slice(end)

      updateAttributes({ [attr]: newValue })

      // Restore cursor position after React re-render
      requestAnimationFrame(() => {
        input.focus()
        const newPos = start + tag.length
        input.setSelectionRange(newPos, newPos)
      })
    },
    [text, url, updateAttributes]
  )

  const handleTextVarSelect = useCallback(
    (variable: Variable) => {
      insertVariableAtCursor(textInputRef, 'text', variable)
      setTextVarOpen(false)
    },
    [insertVariableAtCursor]
  )

  const handleUrlVarSelect = useCallback(
    (variable: Variable) => {
      insertVariableAtCursor(urlInputRef, 'url', variable)
      setUrlVarOpen(false)
    },
    [insertVariableAtCursor]
  )

  return (
    <NodeViewWrapper contentEditable={false}>
      <div className="my-2 border border-gray-200 rounded-lg bg-gray-50 p-3 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Inserir um botão
          </span>
          <button
            type="button"
            onClick={deleteNode}
            className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Text input */}
        <div className="flex items-center gap-1.5">
          <input
            ref={textInputRef}
            type="text"
            value={text}
            onChange={(e) => updateAttributes({ text: e.target.value })}
            placeholder="Texto do botão..."
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
          />
          <Popover open={textVarOpen} onOpenChange={setTextVarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Inserir variável"
              >
                <Braces className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-80" side="top">
              <VariablePicker onSelect={handleTextVarSelect} onClose={() => setTextVarOpen(false)} />
            </PopoverContent>
          </Popover>
        </div>

        {/* URL input */}
        <div className="flex items-center gap-1.5">
          <input
            ref={urlInputRef}
            type="text"
            value={url}
            onChange={(e) => updateAttributes({ url: e.target.value })}
            placeholder="URL do link..."
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
          />
          <Popover open={urlVarOpen} onOpenChange={setUrlVarOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Inserir variável"
              >
                <Braces className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-80" side="top">
              <VariablePicker onSelect={handleUrlVarSelect} onClose={() => setUrlVarOpen(false)} />
            </PopoverContent>
          </Popover>
        </div>

        {/* Preview */}
        {text && (
          <a
            href={url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.preventDefault()}
            className="block text-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium text-sm no-underline hover:bg-blue-700 transition-colors"
          >
            {text}
          </a>
        )}
      </div>
    </NodeViewWrapper>
  )
}
